/* eslint-disable react-hooks/exhaustive-deps */
import {
    Box,
    Button,
    Checkbox,
    ColumnLayout,
    ExpandableSection,
    Flashbar,
    FormField,
    Header,
    Link,
    Select,
    SelectProps,
    Textarea,
} from '@amzn/awsui-components-react';
import React, {ReactNode, useEffect, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {startAmosWorkflow, startBatchAmosWorkflow} from '../../backend/backend';
import {getWorkflowHistoryPageUrl} from '../../backend/internalUrls';
import {StartWorkflowResponse} from '../../backend/model';
import {parseErrorMessage} from '../../utils/errors';
import {exists, isNotBlankOrEmpty, isValidArray, isValidJSON, validate} from '../../validation/InputValidator';
import {regions} from './regions';
import {getWorkflows, Workflow} from './workflows';
import {DropdownStatusProps} from "@amzn/awsui-components-react/polaris/internal/components/dropdown-status/interfaces";

type StartWorkflowPageProps = RouteComponentProps

const StartWorkflowPage: React.FC<StartWorkflowPageProps> = () => {
    const [workflows, setWorkflows] = useState<Workflow[]>()
    const [workflowsLoading, setWorkflowsLoading] = useState<DropdownStatusProps.StatusType>('loading')
    const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>()
    const [selectedWorkflowErrorMsg, setSelectedWorkflowErrorMsg] = useState<string>('')
    const [selectedRegion, setSelectedRegion] = useState<SelectProps.Option | null>()
    const [selectedRegionErrorMsg, setSelectedRegionErrorMsg] = useState<string>('')
    const [isBatchWorkflow, setIsBatchWorkflow] = useState<boolean>(false)
    const [workflowInput, setWorkflowInput] = useState<string>('')
    const [workflowInputErrorMsg, setWorkflowInputErrorMsg] = useState<string>('')
    const [startButtonLoading, setStartButtonLoading] = useState<boolean>(false)
    const [successMessage, setSuccessMessage] = useState<ReactNode>('')
    const [apiErrorMessage, setApiErrorMessage] = useState<ReactNode>('')
    const [workflowLoadError, setWorkflowLoadError] = useState<boolean>(false)

    const availableRegions = regions.filter((region) => {
        return selectedWorkflow?.regions.has(region.value!)
    })
    const isRegionSelectorDisabled = availableRegions.length === 0

    useEffect(() => {
        setWorkflowLoadError(false)
        getWorkflows(false).then(content => {
            let nonDeprecatedWorkflows = content.filter((workflow: Workflow) => !workflow.deprecated)
            setWorkflows(nonDeprecatedWorkflows)
            if (nonDeprecatedWorkflows.length === 0) {
                setWorkflowLoadError(true)
                setApiErrorMessage(
                    <span>
                        <span>You do not have permission to run any workflows. Please contact the </span>
                        <Link href="https://w.amazon.com/bin/view/NetworkTopologyModeling/Contact">Network Topology Modeling team</Link>
                    </span>
                )
                setWorkflowsLoading('error')
            } else {
                setWorkflowsLoading('finished')
            }
        }).catch(error => {
            setWorkflowLoadError(true)
            console.log(error)
            setApiErrorMessage('Error: ' + [parseErrorMessage(error)] + '. No workflows available to start.')
            setWorkflowsLoading('error')
        })
    }, [])

    const resetState = (): void => {
        setSuccessMessage('')
        setApiErrorMessage('')
    }

    function onStartWorkflowClicked(): void {
        const formIsValid = [
            validate(selectedWorkflow, [
                {test: exists, message: 'You must select a workflow'},
            ], setSelectedWorkflowErrorMsg),
            validate(selectedRegion, [
                {test: exists, message: 'You must select a region'},
            ], setSelectedRegionErrorMsg),
            validate(workflowInput, [
                {test: isNotBlankOrEmpty, message: 'You must provide input for the workflow'},
            ], setWorkflowInputErrorMsg),
        ].every(validated => validated === true)

        if (formIsValid) {
            if (!isBatchWorkflow) {
                if (validate(workflowInput, [
                    {test: isValidJSON, message: 'Invalid JSON'},
                ], setWorkflowInputErrorMsg)) {
                    startWorkflow()
                }
            } else {
                if (validate(workflowInput, [
                    {test: isValidArray, message: 'Invalid Array'},
                ], setWorkflowInputErrorMsg)) {
                    startBatchWorkflow()
                }
            }
        }
    }

    function successMessageForExecution(executionId: string): ReactNode {
        return (
            <span>Your workflow has started. (ID: <Link color='inverted' href={getWorkflowHistoryPageUrl(executionId)}>
                    {executionId}
                </Link>)
            </span>
        )
    }

    async function startWorkflow() {
        resetState()
        setStartButtonLoading(true)
        try {
            const response = (await startAmosWorkflow(selectedWorkflow!.value!, workflowInput, selectedRegion!.value!)).data
            if (response.success) {
                setSuccessMessage(successMessageForExecution(response.executionId))
            } else {
                setApiErrorMessage(response.errorMessage)
            }
        } catch (err: any) {
            setApiErrorMessage(parseErrorMessage(err))
        } finally {
            setStartButtonLoading(false)
        }
    }

    function printResponses(responses: StartWorkflowResponse[]): JSX.Element[] {
        return responses.map(response => {
            if (response.success) {
                return (
                    <li>ID: <Link color='inverted' href={getWorkflowHistoryPageUrl(response.executionId)}>
                        {response.executionId}
                    </Link>
                    </li>
                )
            } else {
                return <li>Failed: {response.errorMessage}</li>
            }
        })
    }

    function successMessageForBatchExecution(responses: StartWorkflowResponse[]): ReactNode {
        return <span>Your workflows have started.{printResponses(responses)}</span>
    }

    function failureMessageForBatchExecution(responses: StartWorkflowResponse[]): ReactNode {
        return <span>Errors were detected in your workflows.{printResponses(responses)}</span>
    }

    async function startBatchWorkflow() {
        resetState()
        setStartButtonLoading(true)
        try {
            const responses = (await startBatchAmosWorkflow(selectedWorkflow!.value!, workflowInput, selectedRegion!.value!)).data
            if (responses.some(response => !response.success)) {
                setApiErrorMessage(failureMessageForBatchExecution(responses))
            } else {
                setSuccessMessage(successMessageForBatchExecution(responses))
            }
        } catch (err: any) {
            setApiErrorMessage(parseErrorMessage(err))
        } finally {
            setStartButtonLoading(false)
        }
    }

    const formBody = (
        <>
            <ColumnLayout columns={3}>
                <FormField id="startWorkflowPage-workflow" label="Workflow" errorText={selectedWorkflowErrorMsg}>
                    <Select
                        placeholder="Select"
                        options={workflows}
                        selectedOption={selectedWorkflow as SelectProps.Option}
                        onChange={({detail}) => {
                            setSelectedWorkflowErrorMsg('')
                            setSelectedWorkflow(detail.selectedOption as Workflow)
                            setSelectedRegion(null)
                        }}
                        invalid={!!selectedWorkflowErrorMsg}
                        statusType={workflowsLoading}
                        errorText={"Failed to fetch workflows"}
                        loadingText={"Fetching workflows"}
                    />
                </FormField>
                <FormField id="startWorkflowPage-region" label="Region" errorText={selectedRegionErrorMsg}>
                    <Select
                        placeholder={isRegionSelectorDisabled ? "Select Workflow" : "--"}
                        options={availableRegions}
                        selectedOption={selectedRegion!}
                        onChange={({detail}) => {
                            setSelectedRegionErrorMsg('')
                            setSelectedRegion(detail.selectedOption as SelectProps.Option)
                        }}
                        disabled={isRegionSelectorDisabled}
                        invalid={!!selectedRegionErrorMsg}
                    />
                </FormField>
                <FormField id="startWorkflowPage-batchWorkflow" label="Run in batch">
                    <Checkbox
                        onChange={({detail}) => {
                            setIsBatchWorkflow(detail.checked)
                        }}
                        checked={isBatchWorkflow}
                    />
                </FormField>
            </ColumnLayout>
            <br/>
            <FormField
                id="startWorkflowPage-json"
                label="Parameters"
                errorText={workflowInputErrorMsg}
                stretch>
                <Textarea
                    placeholder={isBatchWorkflow ?
                        selectedWorkflow?.exampleBatchInput?.toString().trim() :
                        selectedWorkflow?.exampleInput?.toString().trim()}
                    value={workflowInput}
                    onChange={
                        ({detail}) => {
                            setWorkflowInputErrorMsg('')
                            setWorkflowInput(detail.value)
                        }
                    }
                    rows={20}
                />
            </FormField>
            <br/>
            <Button
                variant="primary"
                onClick={onStartWorkflowClicked}
                loading={startButtonLoading}
                disabled={workflowLoadError}
            >
                Start
            </Button>
        </>
    )

    return (
        <div>
            <Box variant="h1">Start Workflow</Box>
            <ExpandableSection
                id='startWorkflowPage-form'
                variant='container'
                header={<Header variant='h2'>Input</Header>}
                defaultExpanded>
                {formBody}
            </ExpandableSection>
            <br/>
            {
                successMessage &&
                <Flashbar
                    id='startWorkflowPage-flashbar-success'
                    items={[
                        {
                            type: 'success',
                            content: successMessage,
                            dismissible: true,
                            onDismiss: () => setSuccessMessage(''),
                        },
                    ]}/>
            }
            {
                apiErrorMessage &&
                <Flashbar
                    id='startWorkflowPage-flashbar-error'
                    items={[
                        {
                            type: 'error',
                            content: apiErrorMessage,
                            dismissible: true,
                            onDismiss: () => setApiErrorMessage(''),
                        },
                    ]}
                />
            }
        </div>
    )
}

export default StartWorkflowPage
